"use client";

import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import Image from "next/image";
import { X } from "lucide-react";
import { Popup } from "@/types/popup";

interface PopupManagerProps {
      popups: Popup[];
}

const STORAGE_KEY = "popup_shown_v1";

interface ShownRecord {
      [popupId: string]: { lastShownAt: string };
}

function readShown(): ShownRecord {
      if (typeof window === "undefined") return {};
      try {
            const raw = window.localStorage.getItem(STORAGE_KEY);
            return raw ? (JSON.parse(raw) as ShownRecord) : {};
      } catch {
            return {};
      }
}

function writeShown(record: ShownRecord) {
      try {
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
      } catch {}
}

function isSameDay(a: Date, b: Date): boolean {
      return (
            a.getFullYear() === b.getFullYear() &&
            a.getMonth() === b.getMonth() &&
            a.getDate() === b.getDate()
      );
}

function shouldShow(popup: Popup, shown: ShownRecord): boolean {
      const freq = popup.frequency || "daily";
      const record = shown[popup.documentId];
      if (freq === "always") return true;
      if (!record) return true;
      const lastShown = new Date(record.lastShownAt);
      if (freq === "once") return false;
      if (freq === "daily") return !isSameDay(lastShown, new Date());
      return true;
}

function pickPopup(popups: Popup[]): Popup | null {
      const shown = readShown();
      const eligible = popups.filter((p) => shouldShow(p, shown));
      if (eligible.length === 0) return null;
      const probabilityFiltered = eligible.filter((p) => {
            const prob = typeof p.showProbability === "number" ? p.showProbability : 100;
            if (prob >= 100) return true;
            if (prob <= 0) return false;
            return Math.random() * 100 < prob;
      });
      const pool = probabilityFiltered.length > 0 ? probabilityFiltered : eligible;
      pool.sort((a, b) => (b.priority || 0) - (a.priority || 0));
      return pool[0] || null;
}

function PopupModal({ popup, onClose }: { popup: Popup; onClose: () => void }) {
      const [open, setOpen] = useState(false);

      const requestClose = useCallback(() => {
            setOpen(false);
            window.setTimeout(onClose, 200);
      }, [onClose]);

      useEffect(() => {
            const id = window.requestAnimationFrame(() => setOpen(true));
            const onKey = (e: KeyboardEvent) => {
                  if (e.key === "Escape") requestClose();
            };
            const prevOverflow = document.body.style.overflow;
            document.body.style.overflow = "hidden";
            document.addEventListener("keydown", onKey);
            return () => {
                  window.cancelAnimationFrame(id);
                  document.removeEventListener("keydown", onKey);
                  document.body.style.overflow = prevOverflow;
            };
      }, [requestClose]);

      if (typeof document === "undefined") return null;

      const apiUrl = process.env.NEXT_PUBLIC_API_URL || "";
      const mediaUrl = popup.media?.url
            ? popup.media.url.startsWith("http")
                  ? popup.media.url
                  : `${apiUrl}${popup.media.url}`
            : null;

      return createPortal(
            <div
                  role="dialog"
                  aria-modal="true"
                  aria-label={popup.title || popup.internalName}
                  onClick={requestClose}
                  className={`fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 transition-opacity duration-200 ease-out ${
                        open ? "bg-black/70 opacity-100" : "bg-black/0 opacity-0"
                  }`}
            >
                  <div
                        onClick={(e) => e.stopPropagation()}
                        className={`relative w-full max-w-md bg-white rounded-2xl overflow-hidden shadow-2xl transition-all duration-300 ease-out ${
                              open
                                    ? "opacity-100 scale-100 translate-y-0"
                                    : "opacity-0 scale-95 translate-y-3"
                        }`}
                  >
                        <button
                              type="button"
                              aria-label="Close"
                              onClick={requestClose}
                              className="absolute top-3 right-3 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-white/90 backdrop-blur-md text-gray-700 hover:bg-white hover:text-gray-900 transition-colors cursor-pointer shadow-md"
                        >
                              <X size={18} />
                        </button>

                        {mediaUrl && (
                              <div className="relative w-full aspect-[4/3] bg-gray-100">
                                    <Image
                                          src={mediaUrl}
                                          alt={popup.mediaAlt || popup.media?.alternativeText || popup.title || ""}
                                          fill
                                          className="object-cover"
                                          sizes="(max-width: 640px) 100vw, 28rem"
                                    />
                              </div>
                        )}

                        <div className="p-6 space-y-3 text-center">
                              {popup.title && (
                                    <h2 className="text-2xl font-bold text-gray-900 tracking-tight">
                                          {popup.title}
                                    </h2>
                              )}
                              {popup.description && (
                                    <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">
                                          {popup.description}
                                    </p>
                              )}
                              {popup.buttonText && popup.buttonUrl && (
                                    <div className="pt-2">
                                          <a
                                                href={popup.buttonUrl}
                                                onClick={requestClose}
                                                className="inline-flex items-center justify-center px-7 py-3 text-sm font-semibold text-white bg-[#A68F72] rounded-xl hover:bg-[#947d61] transition-all duration-300 shadow-lg shadow-[#A68F72]/25 hover:-translate-y-0.5"
                                          >
                                                {popup.buttonText}
                                          </a>
                                    </div>
                              )}
                        </div>
                  </div>
            </div>,
            document.body
      );
}

export default function PopupManager({ popups }: PopupManagerProps) {
      const [activePopup, setActivePopup] = useState<Popup | null>(null);

      useEffect(() => {
            if (!popups || popups.length === 0) return;
            const picked = pickPopup(popups);
            if (!picked) return;
            const delayMs = Math.max(0, (picked.delaySeconds ?? 3) * 1000);
            const timer = window.setTimeout(() => {
                  const shown = readShown();
                  shown[picked.documentId] = { lastShownAt: new Date().toISOString() };
                  writeShown(shown);
                  setActivePopup(picked);
            }, delayMs);
            return () => window.clearTimeout(timer);
      }, [popups]);

      if (!activePopup) return null;
      return <PopupModal popup={activePopup} onClose={() => setActivePopup(null)} />;
}
